import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, TrendingUp, Clock, Coins, FileText, Calendar } from "lucide-react";
import { UsageHistoryTable } from "@/components/UsageHistoryTable";
import { PaymentsTable } from "@/components/PaymentsTable";

interface CreditStats {
  totalEarned: number;
  totalSpent: number;
  balance: number;
}

interface UserStats {
  totalProjects: number;
  totalWords: number;
  averageWordsPerDay: number;
  streak: number;
}

export default function AnalyticsPage() {
  const { data: creditStats } = useQuery<CreditStats>({
    queryKey: ["/api/credits/stats"],
  });

  const { data: userStats } = useQuery<UserStats>({
    queryKey: ["/api/users/stats"],
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Track your writing progress, credit usage, and project statistics.
          </p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Credit Balance</CardTitle>
              <Coins className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{creditStats?.balance || 0}</div>
              <p className="text-xs text-muted-foreground">
                Available credits
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
              <FileText className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats?.totalProjects || 0}</div>
              <p className="text-xs text-muted-foreground">
                Stories created
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Words Written</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats?.totalWords?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground">
                Total word count
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Writing Streak</CardTitle>
              <Calendar className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats?.streak || 0}</div>
              <p className="text-xs text-muted-foreground">
                Days in a row
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Credit Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Credit Overview
            </CardTitle>
            <CardDescription>
              Your credit earning and spending patterns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {creditStats?.totalEarned || 0}
                </div>
                <div className="text-sm text-green-700">Total Earned</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {creditStats?.totalSpent || 0}
                </div>
                <div className="text-sm text-red-700">Total Spent</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {creditStats?.balance || 0}
                </div>
                <div className="text-sm text-blue-700">Current Balance</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Tables */}
        <Tabs defaultValue="usage" className="space-y-4">
          <TabsList>
            <TabsTrigger value="usage">Usage History</TabsTrigger>
            <TabsTrigger value="payments">Payment History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="usage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
                <CardDescription>
                  Track your AI model usage and credit consumption
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UsageHistoryTable />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="payments" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Coins className="h-5 w-5" />
                  Payment History
                </CardTitle>
                <CardDescription>
                  Review your subscription and credit purchase history
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PaymentsTable />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
